<template>
  <div class="container mx-auto px-4 py-4 text-gray-900 dark:text-gray-100">
    <PageHeading
      class="mb-6"
      title="Permissions"
      description="Configure access for each role"
      :crumbs="[{ label: 'User Management' }, { label: 'Permissions' }]"
    />

    <div
      class="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900"
    >
      <table class="min-w-full">
        <thead>
          <tr class="border-b border-gray-200 dark:border-gray-700">
            <th
              class="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
            >
              Permission
            </th>
            <th
              v-for="role in roles"
              :key="role.key"
              class="px-4 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400"
            >
              {{ role.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="permission in permissions"
            :key="permission.id"
            class="border-b border-gray-200 last:border-b-0 dark:border-gray-700"
          >
            <td class="px-6 py-4">
              <p class="text-sm font-semibold text-gray-900 dark:text-white">
                {{ permission.name }}
              </p>
              <p class="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {{ permission.description }}
              </p>
            </td>
            <td v-for="role in roles" :key="role.key" class="px-4 py-4 text-center">
              <button
                type="button"
                role="checkbox"
                disabled
                :aria-checked="String(permission.roles[role.key])"
                :aria-label="`${permission.name} for ${role.label}`"
                :data-testid="`permission-${permission.id}-${role.key}`"
                :class="[
                  'inline-flex h-5 w-5 items-center justify-center rounded-md disabled:cursor-not-allowed disabled:opacity-100',
                  permission.roles[role.key]
                    ? 'bg-primary-600 text-white'
                    : 'border border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-800'
                ]"
              >
                <CheckIcon v-if="permission.roles[role.key]" class="h-3.5 w-3.5" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: "AdminPermissions"
};
</script>

<script setup>
import { CheckIcon } from "@heroicons/vue/24/outline";
import PageHeading from "../../components/common/PageHeading.vue";

const roles = [
  { key: "owner", label: "Owner" },
  { key: "admin", label: "Admin" },
  { key: "helper", label: "Helper" },
  { key: "user", label: "User" }
];

const permissions = [
  {
    id: "dashboard",
    name: "View dashboard",
    description: "Dashboard, events, songs, overlays, and statistics",
    roles: { owner: true, admin: true, helper: false, user: true }
  },
  {
    id: "events-songs",
    name: "Manage events and songs",
    description: "Create and edit events and songs",
    roles: { owner: true, admin: true, helper: false, user: true }
  },
  {
    id: "categories",
    name: "Manage categories and genres",
    description: "Create, edit, and delete categories and genres",
    roles: { owner: true, admin: true, helper: false, user: true }
  },
  {
    id: "users",
    name: "Manage users",
    description: "Invite, edit, and deactivate accounts",
    roles: { owner: true, admin: true, helper: false, user: false }
  },
  {
    id: "settings",
    name: "Manage settings",
    description: "Application settings",
    roles: { owner: true, admin: true, helper: false, user: false }
  },
  {
    id: "live",
    name: "Operate live events",
    description: "Live show controls",
    roles: { owner: true, admin: true, helper: true, user: true }
  }
];
</script>
